if (!customElements.get('sender-bundle-picker')) {
  customElements.define(
    'sender-bundle-picker',
    class SenderBundlePicker extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
        if (!this.inputs.length) return;

        if (!this.inputs.some((input) => input.checked)) this.inputs[0].checked = true;

        this.onChange = this.handleChange.bind(this);
        this.addEventListener('change', this.onChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (!variant || !this.closest('product-info')) return;
            this.priceCents = Number(variant.price) || 0;
            this.compareCents = Number(variant.compare_at_price) || this.priceCents;
            this.measurementPriceCents = Number(variant.unit_price) || 0;
            this.measurement = variant.unit_price_measurement || null;
            this.render();
          });
        }

        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      }

      get priceCents() {
        return Number(this.dataset.priceCents) || 0;
      }

      set priceCents(value) {
        this.dataset.priceCents = String(value || 0);
      }

      get compareCents() {
        return Number(this.dataset.compareCents) || this.priceCents;
      }

      set compareCents(value) {
        this.dataset.compareCents = String(value || 0);
      }

      get measurementPriceCents() {
        return Number(this.dataset.measurementPriceCents) || 0;
      }

      set measurementPriceCents(value) {
        this.dataset.measurementPriceCents = String(value || 0);
      }

      get measurement() {
        const unit = this.dataset.measurementReferenceUnit || '';
        if (!unit || this.measurementPriceCents <= 0) return null;
        return {
          reference_value: Number(this.dataset.measurementReferenceValue) || 1,
          reference_unit: unit,
        };
      }

      set measurement(value) {
        this.dataset.measurementReferenceValue = String(value?.reference_value || 1);
        this.dataset.measurementReferenceUnit = value?.reference_unit || '';
      }

      get selectedInput() {
        return this.inputs.find((input) => input.checked) || this.inputs[0];
      }

      handleChange(event) {
        if (!event.target.matches('input[type="radio"]')) return;
        this.render();
      }

      pricingFor(input) {
        const quantity = Math.max(1, Number.parseInt(input.dataset.quantity, 10) || 1);
        const subtotal = this.priceCents * quantity;
        let bundleDiscount = 0;

        if (input.dataset.discountMode === 'percentage') {
          const percentage = Math.min(100, Math.max(0, Number(input.dataset.discountPercent) || 0));
          // Shopify rounds percentage discounts to whole cents per item before
          // calculating the line total. Mirror that rule so product page, cart,
          // and checkout always show the same amount.
          const unitDiscount = Math.floor((this.priceCents * percentage) / 100);
          bundleDiscount = unitDiscount * quantity;
        } else if (input.dataset.discountMode === 'free_units') {
          const freeUnits = Math.min(
            Math.max(0, quantity - 1),
            Math.max(0, Number.parseInt(input.dataset.freeUnits, 10) || 0)
          );
          bundleDiscount = this.priceCents * freeUnits;
        }

        const total = Math.max(0, subtotal - bundleDiscount);
        const compareUnit = Math.max(this.priceCents, this.compareCents);
        const compareTotal = compareUnit * quantity;
        const savings = Math.max(0, compareTotal - total);
        const savingsPercent = compareTotal > 0 ? Math.round((savings / compareTotal) * 100) : 0;
        const measurementPrice = subtotal > 0
          ? Math.floor((this.measurementPriceCents * total) / subtotal)
          : 0;

        return {
          quantity,
          total,
          compareTotal,
          unitPrice: Math.floor(total / quantity),
          measurementPrice,
          savings,
          savingsPercent,
        };
      }

      money(cents) {
        const locale = this.dataset.locale || document.documentElement.lang || 'de-DE';
        const currency = this.dataset.currency || 'EUR';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format((Number(cents) || 0) / 100);
        } catch (_error) {
          return `${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')} ${currency}`;
        }
      }

      measurementLabel(pricing) {
        const measurement = this.measurement;
        if (!measurement || pricing.measurementPrice <= 0) return '';
        const reference = measurement.reference_value === 1 ? '' : `${measurement.reference_value} `;
        return `${this.money(pricing.measurementPrice)}/${reference}${measurement.reference_unit}`;
      }

      render() {
        this.inputs.forEach((input) => {
          const option = input.closest('.sender-bundle-picker__option');
          const pricing = this.pricingFor(input);
          option?.classList.toggle('is-selected', input.checked);

          const price = option?.querySelector('[data-sender-bundle-card-price]');
          const compare = option?.querySelector('[data-sender-bundle-card-compare]');
          if (price) price.textContent = this.money(pricing.total);
          if (compare) {
            compare.textContent = this.money(pricing.compareTotal);
            compare.hidden = pricing.compareTotal <= pricing.total;
          }
        });

        const input = this.selectedInput;
        const pricing = this.pricingFor(input);
        const title = input.dataset.title || `${pricing.quantity}er-Paket`;

        this.syncForm(input, pricing, title);
        this.updateSummary(pricing);
        this.updatePurchasePanel(input, pricing, title);

        this.dispatchEvent(
          new CustomEvent('sender:bundle-change', {
            bubbles: true,
            detail: { input, pricing, title },
          })
        );
      }

      syncForm(input, pricing, title) {
        this.form = document.getElementById(this.dataset.formId) || this.form;
        if (!this.form) return;

        const quantityInput = this.form.querySelector('[data-sender-bundle-quantity]');
        const offerInput = this.form.querySelector('[data-sender-bundle-offer]');
        const discountInput = this.form.querySelector('[data-sender-discount-code]');
        const destinationInput = this.form.querySelector('[data-sender-discount-destination]');

        Array.from(this.form.elements)
          .filter((element) => element.name === 'quantity')
          .forEach((element) => {
            element.value = pricing.quantity;
          });
        if (quantityInput) quantityInput.value = pricing.quantity;
        if (offerInput) offerInput.value = `${title} · ${pricing.quantity} Stück`;
        if (discountInput) discountInput.value = input.dataset.discountCode || '';
        if (destinationInput) destinationInput.value = this.dataset.destination || 'cart';
      }

      updateSummary(pricing) {
        const unitPrice = this.querySelector('[data-sender-bundle-unit-price]');
        const savings = this.querySelector('[data-sender-bundle-saving]');
        const measurement = this.querySelector('[data-sender-bundle-measurement]');

        if (unitPrice) unitPrice.textContent = this.money(pricing.unitPrice);
        if (measurement) {
          const label = this.measurementLabel(pricing);
          measurement.textContent = label;
          measurement.hidden = !label;
        }
        if (savings) {
          savings.textContent = pricing.savings > 0 ? (this.dataset.savingsLabel || '__AMOUNT__').replace('__AMOUNT__', this.money(pricing.savings)) : '';
          savings.hidden = pricing.savings <= 0;
        }
      }

      updatePurchasePanel(input, pricing, title) {
        const info = this.closest('.sender-product-main__info');
        if (!info) return;

        const offer = info.querySelector('[data-sender-purchase-offer]');
        const badge = info.querySelector('[data-sender-purchase-badge]');
        const price = info.querySelector('[data-sender-purchase-price]');
        const compare = info.querySelector('[data-sender-purchase-compare]');
        const unit = info.querySelector('[data-sender-purchase-unit]');
        const measurement = info.querySelector('[data-sender-purchase-measurement]');

        if (offer) offer.textContent = title;
        if (badge) {
          badge.textContent = pricing.savingsPercent > 0 ? `${pricing.savingsPercent} % sparen` : '';
          badge.hidden = pricing.savingsPercent <= 0;
        }
        if (price) price.textContent = this.money(pricing.total);
        if (compare) {
          compare.textContent = this.money(pricing.compareTotal);
          compare.hidden = pricing.compareTotal <= pricing.total;
        }
        if (unit) unit.textContent = `${this.money(pricing.unitPrice)} ${this.dataset.unitLabel || 'pro Dose'}`;
        if (measurement) {
          const label = this.measurementLabel(pricing);
          measurement.textContent = label;
          measurement.hidden = !label;
        }
      }
    }
  );
}
